import { ConcreteExperienceMessage } from '@dadohr/shared/domainTypes/experience';

/**
 * SC-36146 — pure helpers that reason about the *attempts* recorded for one
 * timeline item on one channel. Kept import-light (no firebase/service deps) so
 * they can be unit-tested directly.
 *
 * Every send attempt appends its own concrete message to `messages[]`: a resend
 * does NOT update the failed row, it appends a fresh row with a new `msgId`
 * (the failed one is only flagged `ignore: true` by the issues-bar Retry). A
 * scheduled send that is retried by the worker likewise leaves one ERROR row per
 * failed attempt. So "is this item in error?" must look at the newest attempt on
 * the same channel, not at whether any row ever failed.
 */

/** A newer in-flight attempt stops masking an older error after this long. */
export const RETRY_IN_FLIGHT_MAX_MS = 30 * 60 * 1000;

/** Not yet resolved by a delivery callback (e.g. Twilio `QUEUED`). */
export const isInFlightMessage = (m: ConcreteExperienceMessage): boolean =>
  (m.status as string) === 'QUEUED';

/**
 * Anything neither delivered nor in flight counts as failed — the same rule
 * the issues-bar collector has always used (ERROR, BOUNCED, Twilio UNKNOWN…).
 */
export const isErroredMessage = (m: ConcreteExperienceMessage): boolean =>
  m.status !== 'OK' && !isInFlightMessage(m);

/** Runtime `at` is untrusted (Timestamp, serialized `{_seconds}`, or missing). */
export const getMessageAtMillis = (m: ConcreteExperienceMessage): number => {
  const at = m.at as unknown as
    | { toMillis?: () => number; seconds?: number; _seconds?: number }
    | null
    | undefined;
  if (!at) return 0;
  if (typeof at.toMillis === 'function') return at.toMillis();
  const seconds = at.seconds ?? at._seconds;
  return typeof seconds === 'number' ? seconds * 1000 : 0;
};

/** Which channel delivered the attempt. Email is per sender address. */
export const getMessageChannelKey = (m: ConcreteExperienceMessage): string =>
  m.provider === 'POSTMARK_EMAIL'
    ? `${m.provider}:${m.emailFrom ?? ''}`
    : m.provider;

/**
 * Which timeline item the attempt was for. A message keys off its template id;
 * a task-start keys off the (deduped, sorted) task set it carried, because a
 * resend of a combined task-start re-sends the same set. Null when the row
 * can't be attributed to an item.
 */
export const getMessageItemKey = (m: ConcreteExperienceMessage): string | null => {
  if (m.type === 'EXPERIENCE_MESSAGE') {
    return m.scope?.msgId ? `MESSAGE:${m.scope.msgId}` : null;
  }
  if (m.type === 'TASK_START') {
    const ids = Array.from(new Set(m.scope?.taskIds ?? [])).sort();
    return ids.length ? `TASK_START:${ids.join(',')}` : null;
  }
  return null;
};

/** Item + channel. Attempts with the same key are retries of each other. */
export const getAttemptKey = (m: ConcreteExperienceMessage): string | null => {
  const itemKey = getMessageItemKey(m);
  return itemKey ? `${itemKey}|${getMessageChannelKey(m)}` : null;
};

const laterAttemptsOf = (
  msg: ConcreteExperienceMessage,
  all: ConcreteExperienceMessage[]
): ConcreteExperienceMessage[] => {
  const key = getAttemptKey(msg);
  if (!key) return [];
  const at = getMessageAtMillis(msg);
  return all.filter(
    (m) =>
      m !== msg &&
      m.msgId !== msg.msgId &&
      getAttemptKey(m) === key &&
      getMessageAtMillis(m) >= at
  );
};

/** A later attempt for the same item on the same channel was delivered. */
export const isErrorSuperseded = (
  msg: ConcreteExperienceMessage,
  all: ConcreteExperienceMessage[]
): boolean => laterAttemptsOf(msg, all).some((m) => m.status === 'OK');

/**
 * A later attempt for the same item on the same channel is still being
 * delivered (a retry is in progress). Bounded by RETRY_IN_FLIGHT_MAX_MS so a
 * row that never gets a delivery callback can't hide the error forever.
 */
export const isErrorBeingRetried = (
  msg: ConcreteExperienceMessage,
  all: ConcreteExperienceMessage[],
  nowMillis: number
): boolean =>
  laterAttemptsOf(msg, all).some(
    (m) =>
      isInFlightMessage(m) &&
      nowMillis - getMessageAtMillis(m) <= RETRY_IN_FLIGHT_MAX_MS
  );

export type ErroredAttemptGroup = {
  key: string;
  /** Newest errored attempt — the one the issue row is built from. */
  latest: ConcreteExperienceMessage;
  /** Every errored, not-ignored attempt in the group (incl. `latest`). */
  msgIds: string[];
};

/**
 * Groups the errored, not-ignored attempts that still need attention: one group
 * per item + channel, skipping attempts already superseded by a delivered
 * retry or covered by a retry in flight. Rows that can't be attributed to an
 * item each form their own group (so they still surface).
 */
export const groupOpenErroredAttempts = (
  all: ConcreteExperienceMessage[],
  nowMillis: number
): ErroredAttemptGroup[] => {
  const groups = new Map<string, ErroredAttemptGroup>();
  all.forEach((msg) => {
    if (msg.ignore || !isErroredMessage(msg)) return;
    if (isErrorSuperseded(msg, all) || isErrorBeingRetried(msg, all, nowMillis)) {
      return;
    }
    const key = getAttemptKey(msg) ?? `UNATTRIBUTED:${msg.msgId}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { key, latest: msg, msgIds: [msg.msgId] });
      return;
    }
    existing.msgIds.push(msg.msgId);
    if (getMessageAtMillis(msg) >= getMessageAtMillis(existing.latest)) {
      existing.latest = msg;
    }
  });
  return Array.from(groups.values());
};

/** Returns `messages` with every row whose msgId is in `msgIds` flagged ignored. */
export const markMessagesIgnored = (
  messages: ConcreteExperienceMessage[],
  msgIds: string[]
): ConcreteExperienceMessage[] => {
  const ids = new Set(msgIds);
  return messages.map((m) => (ids.has(m.msgId) ? { ...m, ignore: true } : m));
};

/**
 * Every failed attempt an issue row stands for: the grouped msgIds when the
 * row folds several attempts (see `groupOpenErroredAttempts`), else just the
 * row's own message. Retry and Ignore must both act on all of them, or the
 * next-newest attempt resurfaces as the issue.
 */
export const getIssueAttemptMsgIds = (row: {
  message: ConcreteExperienceMessage;
  groupedMessageIds?: string[];
}): string[] =>
  row.groupedMessageIds?.length ? row.groupedMessageIds : [row.message.msgId];
